import { Schema } from 'effect';

/**
 * Document types supported for conversion.
 *
 * Single source of truth - add new formats here.
 * Type is derived automatically from the schema.
 */
export const DocumentTypeSchema = Schema.Literals([
  'html',
  'pdf',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'txt',
  'md',
  'rtf',
  'epub',
  'mobi',
  'email',
  'email_attachment',
]);

export type DocumentType = Schema.Schema.Type<typeof DocumentTypeSchema>;

export const DOCUMENT_TYPE_BY_EXTENSION = {
  pdf: 'pdf',
  doc: 'doc',
  docx: 'docx',
  ppt: 'ppt',
  pptx: 'pptx',
  txt: 'txt',
  md: 'md',
  markdown: 'md',
  mdx: 'md',
  rtf: 'rtf',
  epub: 'epub',
  mobi: 'mobi',
  html: 'html',
  htm: 'html',
  eml: 'email',
} as const satisfies Record<string, DocumentType>;

export const DOCUMENT_TYPE_BY_MIME = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'text/plain': 'txt',
  'text/markdown': 'md',
  'text/x-markdown': 'md',
  'text/mdx': 'md',
  'application/rtf': 'rtf',
  'text/rtf': 'rtf',
  'application/epub+zip': 'epub',
  'application/x-mobipocket-ebook': 'mobi',
  'text/html': 'html',
  'application/xhtml+xml': 'html',
  'message/rfc822': 'email',
} as const satisfies Record<string, DocumentType>;

/**
 * Canonical Content-Type to serve / set for each document type.
 *
 * The reverse direction of detection: given a known `DocumentType`, what HTTP
 * Content-Type do we serve it as (and set on presigned uploads). Exhaustive
 * over `DocumentType` (`satisfies Record<DocumentType, string>`) so adding a
 * new format forces a Content-Type here. Endpoints that serve or upload
 * documents MUST read from this map rather than keep a divergent copy.
 * `email_attachment` has no single canonical media type, so it is served as
 * generic binary.
 */
export const CONTENT_TYPE_BY_DOCUMENT_TYPE = {
  html: 'text/html',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  md: 'text/markdown',
  rtf: 'application/rtf',
  epub: 'application/epub+zip',
  mobi: 'application/x-mobipocket-ebook',
  email: 'message/rfc822',
  email_attachment: 'application/octet-stream',
} as const satisfies Record<DocumentType, string>;

export function documentTypeFromExtension(
  extension: string | undefined,
): DocumentType | undefined {
  if (!extension) return undefined;
  return DOCUMENT_TYPE_BY_EXTENSION[
    extension.toLowerCase() as keyof typeof DOCUMENT_TYPE_BY_EXTENSION
  ];
}

export function documentTypeFromMime(
  mimeType: string | undefined,
): DocumentType | undefined {
  const baseMime = mimeType?.split(';')[0]?.trim().toLowerCase();
  if (!baseMime) return undefined;
  return DOCUMENT_TYPE_BY_MIME[baseMime as keyof typeof DOCUMENT_TYPE_BY_MIME];
}

export function resolveDocumentTypeForFile(
  filename: string,
  mimeType?: string,
): DocumentType | undefined {
  return (
    documentTypeFromExtension(filename.split('.').pop()) ??
    documentTypeFromMime(mimeType)
  );
}
